/* eslint-disable */

/**
 * Bench bundle setup (rv-guided-bench-variant-setup).
 *
 * Handles the "see all configurations" toggle and keeps the "What's Included"
 * checklist in sync with the selected bundle. Each bundle row carries
 * `data-parts` (space/comma-separated part keys); each checklist row carries
 * `data-part-key`. Variant price/availability still comes from variant-picker.js.
 */
if (!customElements.get('rv-bench-setup')) {
  const BENCH_CHECK =
    '<svg viewBox="0 0 24 24" fill="none" stroke="#1f9d55" stroke-width="1.8"><circle cx="12" cy="12" r="9"/><path d="m8 12 2.5 2.5L16 9" stroke-linecap="round"/></svg>';
  const BENCH_CROSS =
    '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#5f5f5f"/><path d="M8.5 8.5l7 7M15.5 8.5l-7 7" stroke="#fff" stroke-width="1.9" stroke-linecap="round"/></svg>';

  class RvBenchSetup extends HTMLElement {
    connectedCallback() {
      this.includedRows = Array.from(this.querySelectorAll('[data-part-key]'));
      this.moreBtn = this.querySelector('[data-bench-more]');

      this.addEventListener('change', (evt) => {
        if (evt.target.classList.contains('rvct-bench-opt__input')) this.updateIncluded();
      });

      if (this.moreBtn) {
        this.moreBtn.addEventListener('click', () => this.toggleMore());
      }

      this.updateIncluded();
    }

    toggleMore() {
      const expanded = this.classList.toggle('is-expanded');
      this.moreBtn.setAttribute('aria-expanded', String(expanded));
      const label = this.moreBtn.querySelector('[data-bench-more-text]');
      if (!label) return;
      if (!label.dataset.moreText) label.dataset.moreText = label.textContent.trim();
      label.textContent = expanded
        ? this.moreBtn.dataset.lessText || 'Show fewer configurations'
        : label.dataset.moreText;
    }

    updateIncluded() {
      if (!this.includedRows.length) return;
      const checked = this.querySelector('.rvct-bench-opt__input:checked');
      const label = checked && checked.nextElementSibling;
      const raw = label ? label.dataset.parts || '' : '';
      const parts = new Set(
        raw
          .split(/[\s,]+/)
          .map((s) => s.trim().toLowerCase())
          .filter(Boolean)
      );

      this.includedRows.forEach((row) => {
        const on = parts.has((row.dataset.partKey || '').toLowerCase());
        row.classList.toggle('is-off', !on);
        const state = row.querySelector('.rvct-incl__state');
        if (state) state.innerHTML = on ? BENCH_CHECK : BENCH_CROSS;
      });
    }
  }

  customElements.define('rv-bench-setup', RvBenchSetup);
}

/**
 * Cable Tower PDP (A/B test template).
 *
 * Keeps the "Your selected setup" panel and the estimated total in sync with
 * the theme's <variant-picker>. All variant logic still lives in
 * variant-picker.js — this only mirrors the result into the summary panel.
 */
if (!customElements.get('rvct-summary')) {
  class RvctSummary extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-product');
      if (!this.section) return;

      this.picker = this.section.querySelector('variant-picker');
      this.totalEl = this.querySelector('[data-rvct-total]');
      this.prices = this.getPrices();

      this.section.addEventListener('on:variant:change', () => this.render());

      // variant-picker.js upgrades on its own schedule, so wait a tick before
      // reconciling the initial state.
      requestAnimationFrame(() => {
        this.reconcileInitialVariant();
        this.render();
      });
    }

    /**
     * Formatted prices, keyed by variant id.
     * @returns {object}
     */
    getPrices() {
      const el = this.querySelector('[data-rvct-prices]');
      if (!el) return {};

      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return {};
      }
    }

    /**
     * The variant id currently held by the product form.
     * @returns {?string}
     */
    getVariantId() {
      const input = this.section.querySelector('.js-product-form-main input[name="id"]');
      return input ? input.value : null;
    }

    /**
     * The setup cards only expose a subset of each option's values (e.g. the
     * 110 lb stack is hidden), so a `?variant=` deep link can land on a variant
     * that no card represents. When that happens the picker shows one setup and
     * the form holds another — force a change so everything realigns with what
     * the customer can actually see.
     */
    reconcileInitialVariant() {
      if (!this.picker) return;

      const dataEl = this.picker.querySelector('[type="application/json"]');
      const variantId = this.getVariantId();
      if (!dataEl || !variantId) return;

      let variants;
      try {
        variants = JSON.parse(dataEl.textContent).product.variants;
      } catch (e) {
        return;
      }

      const current = variants.find((v) => String(v.id) === String(variantId));
      if (!current) return;

      const selected = Array.from(this.picker.querySelectorAll('.option-selector')).map(
        (selector) => {
          const checked = selector.querySelector('input:checked');
          return checked ? checked.value : null;
        }
      );

      const matches = current.options.every((value, index) => value === selected[index]);
      if (matches) return;

      const trigger = this.picker.querySelector('.option-selector input:checked');
      if (trigger) trigger.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Mirrors each selected option card into its summary row, then refreshes
     * the estimated total.
     */
    render() {
      this.querySelectorAll('[data-rvct-slot]').forEach((slot) => {
        const group = slot.dataset.rvctSlot;
        const selector = this.section.querySelector(`.option-selector[data-rvct-group="${group}"]`);
        if (!selector) return;

        const checked = selector.querySelector('input:checked');
        const card = checked && checked.nextElementSibling;
        if (!card) return;

        const title = slot.querySelector('[data-rvct-slot-title]');
        const sub = slot.querySelector('[data-rvct-slot-sub]');
        const img = slot.querySelector('[data-rvct-slot-image]');

        if (title) title.textContent = card.dataset.summaryTitle || '';
        if (sub) sub.textContent = card.dataset.summarySub || '';

        if (img && card.dataset.summaryImage) {
          img.src = card.dataset.summaryImage;
          img.srcset = card.dataset.summaryImageSrcset || '';
          img.alt = card.dataset.summaryTitle || '';
        }
      });

      if (!this.totalEl) return;
      const price = this.prices[this.getVariantId()];
      if (price) this.totalEl.innerHTML = price;
    }
  }

  customElements.define('rvct-summary', RvctSummary);
}

/**
 * Price block behaviour, per section:
 *
 * - The theme prints the price as one string ("$1,424.99 USD"). The design sets
 *   the ISO code at a smaller size, so the trailing code is wrapped in a span.
 *   variant-picker.js rewrites `.price__current` on every change and again when
 *   its fetch lands, hence the MutationObserver rather than a one-off pass.
 * - The 4-payment amount is looked up from a variant → formatted-price map.
 */
document.querySelectorAll('.rvct .js-product').forEach((section) => {
  const priceContainer = section.querySelector('.rvct-price [data-price]');

  const splitCurrencyCode = () => {
    if (!priceContainer) return;

    priceContainer.querySelectorAll('.price__current, .price__was').forEach((el) => {
      if (el.querySelector('.rvct-price__code')) return;
      const next = el.innerHTML.replace(/\s([A-Za-z]{3})(\s*)$/, '<span class="rvct-price__code">$1</span>');
      if (next !== el.innerHTML) el.innerHTML = next;
    });
  };

  if (priceContainer) {
    splitCurrencyCode();
    new MutationObserver(splitCurrencyCode).observe(priceContainer, {
      childList: true,
      subtree: true
    });
  }

  // The custom "Learn more" links (price installments line + financing block) open
  // the same Shop Pay installments modal as the main product page. That modal belongs
  // to Shopify's <shopify-payment-terms> (rendered hidden in the price block), so
  // forward the click to its own trigger.
  const openInstallmentsModal = (evt) => {
    const terms = section.querySelector('.js-instalments-form shopify-payment-terms');
    if (!terms) return;

    // Shop Pay renders the clickable "learn more" affordance inside the element
    // (light DOM in most builds, shadow DOM in some), so look in both.
    const root = terms.shadowRoot || terms;
    const trigger = root.querySelector('a[href], button, [role="button"]') || terms;

    evt.preventDefault();

    // Replay the exact sequence a real click fires (some builds open the modal
    // on pointer/mouse down rather than `click`). Mirroring the real order means
    // it opens once, like a genuine tap.
    const fire = (Type, type) => {
      try {
        trigger.dispatchEvent(new Type(type, { bubbles: true, cancelable: true, view: window }));
      } catch (e) {
        /* event type unsupported in this browser — skip it */
      }
    };
    const Pointer = window.PointerEvent || MouseEvent;
    fire(Pointer, 'pointerdown');
    fire(MouseEvent, 'mousedown');
    fire(Pointer, 'pointerup');
    fire(MouseEvent, 'mouseup');
    fire(MouseEvent, 'click');
  };

  section
    .querySelectorAll('.rvct-installments__link, .rvct-financing__link')
    .forEach((link) => link.addEventListener('click', openInstallmentsModal));

  const installmentEl = section.querySelector('[data-rvct-installment]');
  const installmentData = section.querySelector('[data-rvct-installment-prices]');
  if (!installmentEl || !installmentData) return;

  let installments;
  try {
    installments = JSON.parse(installmentData.textContent);
  } catch (e) {
    return;
  }

  section.addEventListener('on:variant:change', (evt) => {
    const variant = evt.detail && evt.detail.variant;
    if (!variant) return;

    const amount = installments[variant.id];
    if (amount) installmentEl.textContent = amount;
  });
});
